// Tenantra Inc. - End Tenancy Page (Microsoft 365 Admin Style)
import { useEffect, useState } from 'react';
import { 
  ArrowLeft, Calendar, Home, CheckCircle, AlertTriangle, 
  Star, DollarSign, FileText, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { navigate } from '@/services/navigation';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function DateDropdowns({ value, onChange, error, startYear, endYear, testIdPrefix }) {
  const initParts = value ? value.split('-') : ['', '', ''];
  const [selYear, setSelYear] = useState(initParts[0] || '');
  const [selMonth, setSelMonth] = useState(initParts[1] ? String(parseInt(initParts[1])) : '');
  const [selDay, setSelDay] = useState(initParts[2] ? String(parseInt(initParts[2])) : '');

  const doUpdate = (y, m, d) => {
    if (y && m && d) {
      onChange(`${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`);
    }
  };

  const handleMonth = (v) => { setSelMonth(v); doUpdate(selYear, v, selDay); };
  const handleDay = (v) => { setSelDay(v); doUpdate(selYear, selMonth, v); };
  const handleYear = (v) => { setSelYear(v); doUpdate(v, selMonth, selDay); };

  const sY = startYear || 2020;
  const eY = endYear || new Date().getFullYear() + 2;
  const years = [];
  for (let i = eY; i >= sY; i--) years.push(i);

  const days = [];
  const maxDay = selMonth && selYear ? new Date(parseInt(selYear), parseInt(selMonth), 0).getDate() : 31;
  for (let i = 1; i <= maxDay; i++) days.push(i);

  const sel = "h-9 px-2 text-sm bg-white border border-slate-200 rounded-md text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none";

  return (
    <div className="flex gap-1.5 max-w-[280px]">
      <div className="relative flex-1">
        <select value={selMonth} onChange={(e) => handleMonth(e.target.value)}
          className={`w-full ${sel} ${error ? 'border-red-400' : ''}`} data-testid={`${testIdPrefix}-month`}>
          <option value="">Month</option>
          {MONTHS.map((m, i) => <option key={i} value={String(i+1)}>{m}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
      <div className="relative w-[65px]">
        <select value={selDay} onChange={(e) => handleDay(e.target.value)}
          className={`w-full ${sel} ${error ? 'border-red-400' : ''}`} data-testid={`${testIdPrefix}-day`}>
          <option value="">Day</option>
          {days.map(d => <option key={d} value={String(d)}>{d}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
      <div className="relative w-[80px]">
        <select value={selYear} onChange={(e) => handleYear(e.target.value)}
          className={`w-full ${sel} ${error ? 'border-red-400' : ''}`} data-testid={`${testIdPrefix}-year`}>
          <option value="">Year</option>
          {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
    </div>
  );
}

const LEASE_STATUS_OPTIONS = [
  { value: 'fulfilled', label: 'Lease Fulfilled', desc: 'Completed full term' },
  { value: 'broken', label: 'Lease Broken', desc: 'Left before end' }
];

const RATING_OPTIONS = [
  { value: 5, label: 'Excellent' },
  { value: 4, label: 'Good' },
  { value: 3, label: 'Average' },
  { value: 2, label: 'Below Average' },
  { value: 1, label: 'Poor' }
];

export default function EndTenancyPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tenancy, setTenancy] = useState(null);
  const [tenant, setTenant] = useState(null);

  const [formData, setFormData] = useState({
    move_out_date: '',
    lease_status: 'fulfilled',
    overall_rating: 5,
    damages_amount: '',
    damages_description: '',
    final_notes: '',
    confirm_accurate: false
  });
  const [errors, setErrors] = useState({});

  // Get IDs from URL
  const urlParams = new URLSearchParams(window.location.search);
  const tenancyId = urlParams.get('tenancy');
  const tenantId = window.location.pathname.split('/')[3];

  useEffect(() => {
    if (tenancyId) {
      fetchTenancyDetails();
    }
  }, [tenancyId]);

  const fetchTenancyDetails = async () => {
    try {
      setLoading(true);
      const [tenancyRes, tenantRes] = await Promise.all([
        api.get(`/tenancies/${tenancyId}`),
        api.get(`/tenants/${tenantId}`)
      ]);
      setTenancy(tenancyRes.data);
      setTenant(tenantRes.data);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.move_out_date) {
      newErrors.move_out_date = 'Move-out date is required';
    }
    if (!formData.confirm_accurate) {
      newErrors.confirm_accurate = 'You must confirm the information is accurate';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    try {
      setSubmitting(true);
      
      await api.put(`/tenancies/${tenancyId}`, {
        move_out_date: formData.move_out_date,
        status: 'ended'
      });
      
      await api.post('/reports', {
        tenancy_id: tenancyId,
        report_period: {
          start: tenancy.move_in_date,
          end: formData.move_out_date
        },
        payment: {
          status: 'on_time',
          amount_owing: formData.damages_amount ? parseFloat(formData.damages_amount) : 0
        },
        lease: {
          status: formData.lease_status,
          early_termination: formData.lease_status === 'broken'
        },
        issues: [],
        notes: `Final Report - Rating: ${formData.overall_rating}/5. ${formData.final_notes}${formData.damages_description ? ` Damages: ${formData.damages_description}` : ''}`,
        evidence_file_ids: []
      });
      
      toast.success('Tenancy ended and final report filed');
      navigate('/landlord/reports');
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [year, month, day] = dateStr.split('T')[0].split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  if (!tenancy) {
    return (
      <div className="text-center py-12">
        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
          <FileText className="w-5 h-5 text-slate-400" />
        </div>
        <h3 className="text-base font-medium text-slate-900 mb-1">Tenancy not found</h3>
        <a href="/landlord/reports" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
          Back to reports
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Back Link */}
      <a 
        href="/landlord/reports" 
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to reports
      </a>

      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-slate-800">End Tenancy</h1>
        <p className="text-sm text-slate-500">Submit final report for {tenant?.full_name}</p>
      </div>

      {/* Tenancy Info - Compact */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Current tenancy</span>
        </div>
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Home className="w-4 h-4 text-slate-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800 truncate">{tenancy.property_address}</p>
            <p className="text-xs text-slate-500">Move-in: {formatDate(tenancy.move_in_date)}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Move-out Date */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Move-out details</span>
          </div>
          <div className="p-4">
            <Label htmlFor="move_out_date" className="text-sm text-slate-600">Move-out Date *</Label>
            <div className="mt-1.5">
              <DateDropdowns
                value={formData.move_out_date}
                onChange={(v) => handleChange('move_out_date', v)}
                error={errors.move_out_date}
                startYear={2020}
                endYear={new Date().getFullYear() + 2}
                testIdPrefix="end-move-out"
              />
            </div>
            {errors.move_out_date && (
              <p className="text-red-500 text-xs mt-1">{errors.move_out_date}</p>
            )}
          </div>
        </div>

        {/* Lease Status */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Lease status</span>
          </div>
          <div className="p-4">
            <div className="flex gap-3">
              {LEASE_STATUS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('lease_status', option.value)}
                  className={`flex-1 px-3 py-2.5 rounded-md border text-left transition-all ${
                    formData.lease_status === option.value
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <p className="text-sm font-medium text-slate-800">{option.label}</p>
                  <p className="text-xs text-slate-500">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Overall Rating - Compact */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Overall rating</span>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-2">
              {RATING_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('overall_rating', option.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm transition-all ${
                    formData.overall_rating === option.value
                      ? 'border-teal-500 bg-teal-50 text-teal-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Star className={`w-4 h-4 ${formData.overall_rating === option.value ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                  {option.value}
                </button>
              ))}
              <span className="text-sm text-slate-500 ml-2">
                {RATING_OPTIONS.find(r => r.value === formData.overall_rating)?.label}
              </span>
            </div>
          </div>
        </div>

        {/* Damages - Optional */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Damages & amounts owing</span>
            <span className="text-xs text-slate-400 ml-1">(optional)</span>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <Label htmlFor="damages_amount" className="text-sm text-slate-600">Amount ($CAD)</Label>
              <div className="relative mt-1.5 max-w-[160px]">
                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="damages_amount"
                  type="number"
                  step="0.01"
                  value={formData.damages_amount}
                  onChange={(e) => handleChange('damages_amount', e.target.value)}
                  className="h-9 pl-8 text-sm bg-white border-slate-200"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="damages_description" className="text-sm text-slate-600">Description</Label>
              <textarea
                id="damages_description"
                value={formData.damages_description}
                onChange={(e) => handleChange('damages_description', e.target.value)}
                rows={2}
                className="mt-1.5 w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-md placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Final Notes */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Final notes</span>
            <span className="text-xs text-slate-400 ml-1">(optional)</span>
          </div>
          <div className="p-4">
            <textarea
              value={formData.final_notes}
              onChange={(e) => handleChange('final_notes', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-md placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Confirmation - Compact alert style */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="confirm"
              checked={formData.confirm_accurate}
              onCheckedChange={(checked) => handleChange('confirm_accurate', checked)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <Label htmlFor="confirm" className="text-sm text-slate-800 font-medium cursor-pointer">
                I confirm this information is accurate *
              </Label>
              <p className="text-xs text-slate-600 mt-0.5">
                This final report will be permanently added to the tenant's rental history.
              </p>
              {errors.confirm_accurate && (
                <p className="text-red-500 text-xs mt-1">{errors.confirm_accurate}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions - Text link style */}
        <div className="flex items-center justify-between pt-2">
          <a 
            href="/landlord/reports"
            className="text-sm text-slate-500 hover:text-slate-700 font-medium"
          >
            Cancel
          </a>
          <Button 
            type="submit" 
            disabled={submitting}
            className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium h-9 px-4"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4" />
                End Tenancy
              </span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
